/* nc-90-days — First 90 Days dark timeline.
   Desktop: one-shot comet sweep on scroll-into-view. Mobile: continuous scroll-scan vertical rail.
   Aurora mouse-parallax (pointer:fine + motion-allowed). Ported from the source DCLogic.
   Self-contained, per-instance, no globals. */
(function () {
  'use strict';

  function init(root) {
    if (root.__ncVmbReady) return;
    root.__ncVmbReady = true;

    var reduceMQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var bgMotion = root.getAttribute('data-bg-motion') !== 'false';
    var showSpine = root.getAttribute('data-show-spine') !== 'false';
    var sweepMs = (parseFloat(root.getAttribute('data-sweep')) || 6.8) * 1000;

    var cols = Array.prototype.slice.call(root.querySelectorAll('.vmb-col'));

    // per-instance state
    var spineRaf = 0, raf2 = 0, onScroll = null, mql = null, onMql = null, onMove = null, swept = false;

    function revealCol(i) { if (cols[i]) cols[i].classList.add('is-shown'); }

    function teardownMode() {
      if (spineRaf) { cancelAnimationFrame(spineRaf); spineRaf = 0; }
      if (raf2) { cancelAnimationFrame(raf2); raf2 = 0; }
      if (onScroll) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        onScroll = null;
      }
    }

    function startDesktop() {
      var rail = root.querySelector('.vmb-rail');
      if (!rail) { cols.forEach(function (c) { c.classList.add('is-shown'); }); return; }
      var comet = rail.querySelector('.vmb-comet');
      var fill = rail.querySelector('.vmb-fill');
      var nodes = Array.prototype.slice.call(rail.querySelectorAll('.vmb-node'));
      if (!comet || nodes.length < 2) { cols.forEach(function (c) { c.classList.add('is-shown'); }); return; }
      nodes.forEach(function (nd) { nd.classList.remove('is-lit', 'is-end'); });
      if (fill) { fill.style.width = '0'; fill.style.opacity = '0'; }
      comet.style.opacity = '0';
      var n = nodes.length;
      // measure real node centres so track/fill/comet land on them exactly (gap-aware, any node count)
      var railRect = rail.getBoundingClientRect();
      var railW = railRect.width || 1;
      var r0 = nodes[0].getBoundingClientRect();
      var rN = nodes[n - 1].getBoundingClientRect();
      var START = ((r0.left + r0.width / 2 - railRect.left) / railW) * 100;
      var SPAN = ((rN.left + rN.width / 2 - railRect.left) / railW) * 100 - START;
      if (!(SPAN > 0)) { START = 16.6667; SPAN = 66.6666; }
      rail.style.setProperty('--rail-start', START + '%');
      rail.style.setProperty('--rail-span', SPAN + '%');
      var period = sweepMs, fade = 0.06;
      // already swept (e.g. mobile->desktop mode round-trip): paint the completed end-state, don't blank the rail
      if (swept) {
        nodes.forEach(function (nd) { nd.classList.add('is-lit'); });
        nodes[n - 1].classList.add('is-end');
        if (fill) { fill.style.width = SPAN + '%'; fill.style.opacity = '1'; }
        cols.forEach(function (c) { c.classList.add('is-shown'); });
        return;
      }
      var start = null;
      function frame(now) {
        if (start === null) start = now;
        var p = (now - start) / period;
        var done = p >= 1;
        if (p > 1) p = 1;
        var posPct = START + p * SPAN;
        var cometOp = 1;
        if (p < fade) cometOp = p / fade;
        else if (p > 1 - fade) cometOp = (1 - p) / fade;
        var fillOp = p < fade ? p / fade : 1;
        comet.style.left = posPct + '%';
        comet.style.opacity = cometOp.toFixed(3);
        if (fill) { fill.style.width = (p * SPAN) + '%'; fill.style.opacity = fillOp.toFixed(3); }
        for (var i = 0; i < n; i++) {
          var np = i / (n - 1);
          if (p >= np - 0.02) { nodes[i].classList.add('is-lit'); revealCol(i); }
        }
        if (!done) { spineRaf = requestAnimationFrame(frame); }
        else {
          comet.style.opacity = '0';
          if (fill) { fill.style.width = SPAN + '%'; fill.style.opacity = '1'; }
          nodes[n - 1].classList.add('is-end');
          revealCol(n - 1);
          if (onScroll) { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); onScroll = null; }
        }
      }
      function startSweep() { start = null; spineRaf = requestAnimationFrame(frame); }
      function inView() {
        var r = root.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight || 800;
        return r.top < vh * 0.85 && r.bottom > vh * 0.15;
      }
      function maybeStart() {
        if (swept) return;
        if (inView()) { swept = true; startSweep(); }
      }
      onScroll = function () {
        if (raf2) return;
        raf2 = requestAnimationFrame(function () { raf2 = 0; maybeStart(); });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      maybeStart();
      [120, 400, 900, 1500].forEach(function (ms) { setTimeout(maybeStart, ms); });
    }

    function startMobile() {
      var grid = root.querySelector('.vmb-grid');
      if (!grid) { cols.forEach(function (c) { c.classList.add('is-shown'); }); return; }
      var fill = grid.querySelector('.vmb-vfill');
      var track = grid.querySelector('.vmb-vtrack');
      var mnodes = Array.prototype.slice.call(grid.querySelectorAll('.vmb-mnode'));
      if (!mnodes.length) { cols.forEach(function (c) { c.classList.add('is-shown'); }); return; }
      revealCol(0); // first phase always present
      function update() {
        var gr = grid.getBoundingClientRect();
        function yrel(i) { return mnodes[i].getBoundingClientRect().top - gr.top + 8; }
        var y0 = yrel(0), yN = yrel(mnodes.length - 1);
        if (track) { track.style.top = y0 + 'px'; track.style.height = Math.max(0, yN - y0) + 'px'; }
        if (fill) { fill.style.top = y0 + 'px'; }
        var vh = window.innerHeight || document.documentElement.clientHeight || 800;
        var scan = vh * 0.62;
        mnodes.forEach(function (nd, i) {
          var lit = nd.getBoundingClientRect().top < scan;
          nd.classList.toggle('is-lit', lit);
          if (lit) revealCol(i);
        });
        var scanRel = scan - gr.top;
        var h = Math.min(yN - y0, scanRel - y0);
        if (h < 0) h = 0;
        if (fill) fill.style.height = h + 'px';
      }
      onScroll = function () {
        if (raf2) return;
        raf2 = requestAnimationFrame(function () { raf2 = 0; update(); });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
      [120, 400, 900].forEach(function (ms) { setTimeout(update, ms); });
    }

    function setupMode() {
      teardownMode();
      if (mql && mql.matches) startMobile();
      else startDesktop();
    }

    function initSpine() {
      var reduce = reduceMQ && reduceMQ.matches;
      var active = bgMotion && showSpine && !reduce;
      if (!active) { cols.forEach(function (c) { c.classList.add('is-shown'); }); return; }
      root.classList.add('vmb-armed');
      mql = window.matchMedia('(max-width:820px)');
      onMql = function () { setupMode(); };
      try { mql.addEventListener('change', onMql); } catch (e) {}
      setupMode();
    }

    function initParallax() {
      if (!bgMotion) return;
      var aurora = root.querySelector('.vmb-aurora');
      if (!aurora) return;
      var mm = window.matchMedia;
      if (mm && (mm('(prefers-reduced-motion: reduce)').matches || !mm('(pointer:fine)').matches)) return;
      var raf = 0, tx = 0, ty = 0;
      onMove = function (e) {
        var r = root.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width - 0.5) * 20;
        ty = ((e.clientY - r.top) / r.height - 0.5) * 16;
        if (!raf) raf = requestAnimationFrame(function () { aurora.style.transform = 'translate(' + tx + 'px,' + ty + 'px)'; raf = 0; });
      };
      root.addEventListener('mousemove', onMove);
    }

    initParallax();
    initSpine();

    root.__ncVmbTeardown = function () {
      teardownMode();
      if (mql && onMql) { try { mql.removeEventListener('change', onMql); } catch (e) {} }
      if (onMove) root.removeEventListener('mousemove', onMove);
    };
  }

  function boot() {
    document.querySelectorAll('[data-nc-90]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('[data-nc-90]') : null;
    if (el) init(el);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('[data-nc-90]') : null;
    if (el && typeof el.__ncVmbTeardown === 'function') el.__ncVmbTeardown();
  });
})();
